import { useEffect, useRef, useState, useCallback } from 'react';
import { generate } from "random-words";
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useThread } from '../Contexts/ThreadContext';
import { ChartData } from '../types/chat';


interface ChatResponse {
    content: string;
    response_type?: 'agent_transfer' | 'tool_call';
    agent_name: string
    timestamp?: string;
}

export const useWebSocket = (url: string) => {
    const { threadId, isNewChat, threaddbId, agentMode, sourceId, sourceTitle } = useThread();
    const [response, setResponse] = useState<ChatResponse>({
        content: '',
        response_type: 'agent_transfer',
        agent_name: '',
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isBotResponseComplete, setIsBotResponseComplete] = useState<boolean>(false);
    const [thinkingSteps, setThinkingSteps] = useState<string[]>([]);
    const [chartMessage, setChartMessage] = useState<{ chartData: ChartData; timestamp: string } | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const wordUUIDRef = useRef<string>((generate({ exactly: 4 }) as string[]).join('-'));
    const messageQueueRef = useRef<string[]>([]);
    const retryCountRef = useRef<number>(0);
    const maxRetries = 5;
    const customId = wordUUIDRef;
    const token = useSelector((state: any) => state.auth.token);
    const userId = useSelector((state: any) => state.auth.userId);
    // When true, all incoming WebSocket frames are silently dropped until the
    // next sendMessage call. This prevents stale chunks from a cancelled query
    // from appearing in the UI.
    const ignoreIncomingRef = useRef<boolean>(false);

    const connectSocket = useCallback(() => {
        const socket = new WebSocket(url);
        socketRef.current = socket;
        socket.onopen = () => {

            console.log('WebSocket connection opened for', customId);
            setIsOpen(true);
            retryCountRef.current = 0;
            if (threadId){
                socket.send(JSON.stringify({ init: true, token, userId, threadId, new_chat: isNewChat, threaddbId, agent_mode: agentMode, source_id: sourceId, source_title: sourceTitle })); // Send initial connection message
            }

            // Send queued messages once connection is open
            while (messageQueueRef.current.length > 0) {
                const message = messageQueueRef.current.shift();
                if (message) {
                    sendMessage(message);
                }
            }
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                // Drop stale frames from a cancelled query.
                if (ignoreIncomingRef.current && !data.cancelled) return;

                try {
                    if (data.response_type === 'thinking') {
                        setThinkingSteps(prev => [...prev, data.content]);
                    } else if (data.response_type === 'chart') {
                        setChartMessage({ chartData: data.chartData, timestamp: data.timestamp });
                    } else {
                        setResponse((prevResponse) => ({
                            ...prevResponse,
                            content:
                                prevResponse.agent_name === data.agent_name
                                    ? prevResponse.content + (data.content || '')
                                    : data.content || '',
                            type: data.response_type,
                            agent_name: data.agent_name || prevResponse.agent_name,
                        }));
                    }
                } catch (error) {
                    console.error('Failed to parse on_chat_model_stream as JSON:', error);
                }
                if (data.on_chat_model_end) {
                    setIsBotResponseComplete(true);
                }
            } catch (error) {
                console.log('Error parsing WebSocket message:', error);
            }
        };

        socket.onclose = () => {
            console.log('WebSocket connection closed');
            setIsOpen(false);
            if (retryCountRef.current < maxRetries) {
                retryCountRef.current += 1;
                console.log(`Attempting to reconnect (${retryCountRef.current}/${maxRetries}) in 3 seconds...`);
                setTimeout(connectSocket, 3000); // Retry connection after a 3 sec delay
            } else {
                console.log('Max reconnection attempts reached. Stopping reconnection.');
            }
        };

        socket.onerror = (error) => {
            console.log('WebSocket error:', error);
        };
    }, [url]); // watches changes to url or App.tsx setting the easter egg

    useEffect(() => {
        connectSocket();

        return () => {
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close(); // Close connection on cleanup
            }
        };
    }, [connectSocket]);


    const sendMessage = (message: string, overrides?: { threadId?: string; threaddbId?: string; isNewChat?: boolean }) => {
        ignoreIncomingRef.current = false; // re-enable incoming frames for the new request
        const effectiveThreadId = overrides?.threadId ?? threadId;
        const effectiveThreaddbId = overrides?.threaddbId ?? threaddbId;
        const effectiveIsNewChat = overrides?.isNewChat ?? isNewChat;
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            const payload = {
                userId,
                message,
                init: false,
                token,
                threadId: effectiveThreadId,
                new_chat: effectiveIsNewChat,
                threaddbId: effectiveThreaddbId,
                agent_mode: agentMode,
                source_id: sourceId,
                source_title: sourceTitle,
            };

            if (!effectiveThreadId) {
                console.error('Thread ID is not set. Cannot send message.');
                return;
            }
            socketRef.current.send(JSON.stringify(payload)); // Send message to WebSocket server
            setIsBotResponseComplete(false);
            setThinkingSteps([]);

            // Clear previous response before new message is processed
            setResponse({
                content: '',
                response_type: 'agent_transfer', // or 'tool', depending on your default
                agent_name: '',
            });
        } else {
            console.log('WebSocket connection is not open, queuing message:', message);
            messageQueueRef.current.push(message); // Queue message if connection is closed
        }
    };
    const sendJson = (json: object) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(json)); // Send JSON object to WebSocket server
        } else {
            console.log('WebSocket connection is not open, cannot send JSON:', json);
        }
    };

    const cancelQuery = () => {
        ignoreIncomingRef.current = true;
        setResponse({ content: '', response_type: 'agent_transfer', agent_name: '' });
        setThinkingSteps([]);
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ cancel: true, userId, threadId, threaddbId }));
        }
    };

    return { response, isOpen, isBotResponseComplete, thinkingSteps, chartMessage, sendMessage, sendJson, cancelQuery };
};
